// -----------------------------------------------------------
// FloraIQ — Validação e Qualidade dos Dados (Página Principal)
// -----------------------------------------------------------
// Fonte real: GET /quality/issues (view qualidade.v_validation_issues_sql) e
// GET /quality/summary. Secoes sem fonte real mostram NoRealData.

import { useEffect, useState } from "react";
import PageHeader from "@/components/shared/PageHeader";
import KpiCard from "@/components/shared/KpiCard";
import { NoRealData } from "@/components/shared";
import { useQualityIssuesList } from "@/hooks";
import { api } from "@/lib/api";
import { QUALITY_RULES, qualityRuleCodesOfGroup } from "@/config/qualidade-3d-config";
import QualityOverview from "./components/QualityOverview";
import ValidationRulesTable from "./components/ValidationRulesTable";
import InconsistenciesTable from "./components/InconsistenciesTable";

type TabId = "overview" | "rules" | "inconsistencies" | "gis" | "arboreo" | "sources" | "rrr" | "nuvem" | "correcoes" | "reports";

const tabs: { id: TabId; label: string; icon: string }[] = [
  { id: "overview", label: "Visão Geral", icon: "ri-eye-line" },
  { id: "rules", label: "Regras de Validação", icon: "ri-list-check-3" },
  { id: "inconsistencies", label: "Inconsistências", icon: "ri-error-warning-line" },
  { id: "gis", label: "Validação GIS", icon: "ri-map-pin-line" },
  { id: "arboreo", label: "Validação Arbórea", icon: "ri-plant-line" },
  { id: "sources", label: "Fontes e Evidências", icon: "ri-folder-info-line" },
  { id: "rrr", label: "RRR e Autorizações", icon: "ri-shield-check-line" },
  { id: "nuvem", label: "Nuvem de Pontos", icon: "ri-stack-line" },
  { id: "correcoes", label: "Correções Assistidas", icon: "ri-tools-line" },
  { id: "reports", label: "Relatórios", icon: "ri-file-chart-line" },
];

export default function DataQualityPage() {
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const { items: issues, loading, error } = useQualityIssuesList();
  const [projectNames, setProjectNames] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    api
      .projects()
      .then((rows) => {
        if (cancelled) return;
        setProjectNames(Object.fromEntries(rows.map((p) => [String(p.project_id), p.name])));
      })
      .catch(() => {
        // Sem nomes, a tabela mostra "Projeto #id".
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const countBy = (sev: string) => issues.filter((i) => i.severidade === sev).length;
  const kpi = (n: number) => (loading ? "…" : error ? "—" : n.toLocaleString("pt-BR"));

  const tableProps = { issues, loading, error, projectNames };

  const renderTab = () => {
    switch (activeTab) {
      case "overview":
        return <QualityOverview issues={issues} loading={loading} error={error} />;
      case "rules":
        return <ValidationRulesTable issues={issues} loading={loading} error={error} />;
      case "inconsistencies":
        return <InconsistenciesTable {...tableProps} />;
      case "gis":
        return (
          <NoRealData
            title="Validação GIS"
            icon="ri-map-pin-line"
            description="Nenhuma regra geométrica (geometria ausente, coordenada fora do município, duplicidade espacial) está implementada na view de qualidade ainda."
          />
        );
      case "arboreo":
        return <InconsistenciesTable {...tableProps} ruleCodes={qualityRuleCodesOfGroup("arboreo")} emptyTitle="Nenhuma inconsistência do cadastro arbóreo" />;
      case "sources":
        return <InconsistenciesTable {...tableProps} ruleCodes={qualityRuleCodesOfGroup("fontes")} emptyTitle="Nenhuma inconsistência de fontes e evidências" />;
      case "rrr":
        return <InconsistenciesTable {...tableProps} ruleCodes={qualityRuleCodesOfGroup("rrr")} emptyTitle="Nenhuma inconsistência de RRR e autorizações" />;
      case "nuvem":
        return <InconsistenciesTable {...tableProps} ruleCodes={qualityRuleCodesOfGroup("nuvem")} emptyTitle="Nenhuma inconsistência de nuvem de pontos" />;
      case "correcoes":
        return (
          <NoRealData
            title="Correções assistidas"
            icon="ri-tools-line"
            description="Não há fluxo de correção em lote no backend. Cada inconsistência é resolvida corrigindo o registro na tela do módulo correspondente."
          />
        );
      case "reports":
        return (
          <NoRealData
            title="Relatórios de qualidade"
            icon="ri-file-chart-line"
            description="A geração de relatórios de qualidade ainda não foi implementada."
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Validação e Qualidade dos Dados"
        subtitle="Inconsistências detectadas pelas regras de qualidade do banco sobre os dados do CTA e da metodologia FloraIQ-LADM."
      />

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <KpiCard label="Inconsistências Abertas" value={kpi(issues.length)} icon="ri-error-warning-line" variant="default" />
        <KpiCard label="Críticas" value={kpi(countBy("crítica"))} icon="ri-close-circle-line" variant="danger" />
        <KpiCard label="Altas" value={kpi(countBy("alta"))} icon="ri-alert-line" variant="warning" />
        <KpiCard label="Regras Implementadas" value={QUALITY_RULES.length} icon="ri-list-check-3" variant="info" />
      </div>

      {/* Aviso conceitual */}
      <div className="bg-blue-50/60 border border-blue-200/60 rounded-card p-4 text-sm text-foreground-600">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-information-line text-blue-600"></i>
          </div>
          <p>
            As inconsistências são calculadas a cada consulta pelas regras implementadas no banco. Uma pendência desaparece
            quando o registro é corrigido no módulo de origem.
          </p>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white rounded-card border border-background-200/70 overflow-hidden">
        <div className="border-b border-background-200/50 overflow-x-auto scrollbar-thin">
          <div className="flex px-1 pt-1 gap-0.5 min-w-max">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium whitespace-nowrap transition-all duration-200 cursor-pointer rounded-t-md border-b-2 -mb-[1px] ${
                  activeTab === tab.id
                    ? "text-blue-600 border-blue-500 bg-blue-50/50"
                    : "text-foreground-500 border-transparent hover:text-foreground-700 hover:bg-background-50/50"
                }`}
              >
                <i className={`${tab.icon} text-sm`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-5 fade-in" key={activeTab}>
          {renderTab()}
        </div>
      </div>
    </div>
  );
}
